// Copied from frontend-admin/src/utils/fare.js.
// Edit there first, then re-copy. A change made only here is drift.
//
// The customer app is the third caller. A fee quoted at checkout, the fee shown
// on the order, and the fee the partner sees on their own screen are one number
// or they are a support conversation.

// The fare formula, as one pure function.
//
// It lives here rather than inside the Settings screen because pricing is not a
// Settings concern. `frontend-partner` will price an order with it, and the
// order detail screen will explain a fee with it — a second implementation of
// the same arithmetic is how two screens start disagreeing about what a
// delivery costs, and the customer is the one who notices.
//
// The five inputs use frontend-partner/SPEC.md's names, so that app inherits
// this rather than inventing a second vocabulary for the same numbers.

// Money rounds to centavos, distance rounds to metres. They are not the same
// rounder: rounding a distance to two places makes anything under ten metres
// free, which is how a metre past the included distance came out costing
// nothing. Both exist only to keep float noise out — 3.4 - 2 is
// 1.4000000000000004 without them.
const pesos = (value) => Math.round(value * 100) / 100
const metres = (value) => Math.round(value * 1000) / 1000

/**
 * What a delivery of `distanceKm` costs under `rules`.
 *
 * Returns `null` for a distance that is not a number.
 *
 * **There is no distance ceiling.** `maxDistanceKm` used to sit here and refuse anything
 * past the partner's own limit. The service area already answers the only question that
 * needs answering — both ends inside Los Baños or Bay — and the longest trip that passes
 * it is 15 km, where the default ceiling was 8. What the ceiling did was refuse the
 * best-paying work in the system: the commission is flat, so a 15 km delivery leaves the
 * rider ₱170 against ₱14 on a 2 km one.
 *
 * `refused` survives in the shape, always false, because screens read it.
 */
export default function fareFor(distanceKm, rules) {
  const { baseFare, kmIncludedInBase, ratePerKm, minimumFare } = rules

  // Checked before Number(), which turns '' into 0 — an empty box is a question
  // nobody has answered yet, not a delivery of no distance.
  if (distanceKm === '' || distanceKm == null) return null

  const distance = Number(distanceKm)
  if (!Number.isFinite(distance) || distance < 0) return null

  // Everything past what the base already covers. Negative is not a discount.
  const chargeableKm = metres(Math.max(0, distance - kmIncludedInBase))
  const distanceCharge = pesos(chargeableKm * ratePerKm)
  const computed = pesos(baseFare + distanceCharge)

  // The floor applies last, so a short trip cannot come out under it.
  const total = Math.max(computed, minimumFare)

  return {
    refused: false,
    distance,
    chargeableKm,
    distanceCharge,
    computed,
    total: pesos(total),
    liftedToMinimum: total > computed,
  }
}
